import type { ConsumerApiClient } from '../client';

// ─── Types ──────────────────────────────────────────────────

export interface AlbumPhoto {
  id: string;
  s3Key: string;
  thumbnailUrl: string | null;
  contactId: string | null;
  contactName: string | null;
  source: string;
  notes: string | null;
  moderationStatus: 'pending' | 'pass' | 'warn' | 'rejected';
  timesSent: number;
  lastSentAt: string | null;
  lastSentToContactName: string | null;
  createdAt: string;
}

export interface AlbumStats {
  totalPhotos: number;
  totalPhotosEver: number;
  photosSent: number;
  lastPhotoAddedAt: string | null;
  lastPhotoSentAt: string | null;
  photosByContact: {
    contactId: string | null;
    contactName: string;
    count: number;
  }[];
}

export interface AlbumListResponse {
  data: AlbumPhoto[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface AddAlbumPhotoInput {
  s3Key: string;
  contactId?: string;
  source: string;
  notes?: string;
  fileSizeBytes?: number;
  originalFilename?: string;
}

export interface GetAlbumPhotosParams {
  contactId?: string;
  page?: number;
  limit?: number;
  sort?: 'newest' | 'oldest';
  status?: 'sent' | 'unsent';
}

export interface AddAlbumPhotosResponse {
  data: AlbumPhoto[];
  albumStats: { totalPhotos: number; lastPhotoAddedAt: string };
}

export interface DeleteAlbumPhotosResponse {
  deleted: number;
  albumStats: { totalPhotos: number };
}

export interface SendAlbumToCartResponse {
  addedToCart: number;
  cartItemCount: number;
}

// ─── API ────────────────────────────────────────────────────

export interface AlbumApi {
  /** List album photos with optional filters. */
  getPhotos(params?: GetAlbumPhotosParams): Promise<AlbumListResponse>;
  /** Add photos to album. Photos must already be uploaded to S3. */
  addPhotos(photos: AddAlbumPhotoInput[]): Promise<AddAlbumPhotosResponse>;
  /** Soft-delete photos from album. */
  deletePhotos(photoIds: string[]): Promise<DeleteAlbumPhotosResponse>;
  /** Update metadata on a single album photo. */
  updatePhoto(
    photoId: string,
    updates: { contactId?: string | null; notes?: string },
  ): Promise<AlbumPhoto>;
  /** Get aggregated album stats for the current user. */
  getStats(): Promise<AlbumStats>;
  /**
   * Move album photos into the active cart for checkout.
   * Photos remain in album after sending (for re-use).
   */
  sendToCart(
    photoIds: string[],
    size: string,
    finish: string,
  ): Promise<SendAlbumToCartResponse>;
}

export function createAlbumApi(client: ConsumerApiClient): AlbumApi {
  return {
    getPhotos(params) {
      const query = new URLSearchParams();
      if (params?.contactId) query.set('contactId', params.contactId);
      if (params?.page) query.set('page', String(params.page));
      if (params?.limit) query.set('limit', String(params.limit));
      if (params?.sort) query.set('sort', params.sort);
      if (params?.status) query.set('status', params.status);
      const qs = query.toString();
      return client.get(`/album/photos${qs ? `?${qs}` : ''}`);
    },

    addPhotos(photos) {
      return client.post('/album/photos', { photos });
    },

    deletePhotos(photoIds) {
      return client.delete('/album/photos', { photoIds });
    },

    updatePhoto(photoId, updates) {
      return client.patch(`/album/photos/${photoId}`, updates);
    },

    getStats() {
      return client.get('/album/stats');
    },

    sendToCart(photoIds, size, finish) {
      return client.post('/album/photos/send', { photoIds, size, finish });
    },
  };
}
